<template>
  <div class="cj-upload">
    <!-- 已上传文件回显列表（自定义渲染，不依赖 el-upload 内部状态） -->
    <div v-if="innerList.length" class="cj-upload__list">
      <!-- 图片类型：缩略图卡片 -->
      <div v-if="isImage" class="cj-upload__item cj-upload__item--card">
        <div v-for="(item, idx) in innerList" :key="idx" class="cj-upload__thumb">
          <img :src="item.url" @click="handlePreview(item)" />
          <span class="cj-upload__actions">
            <i class="el-icon-zoom-in" title="预览" @click="handlePreview(item)"></i>
            <i v-if="!disableds" class="el-icon-delete" title="删除" @click="handleRemove(item, idx)"></i>
          </span>
        </div>
      </div>
      <!-- 非图片类型：文件名列表 -->
      <ul v-else class="cj-upload__file-list">
        <li v-for="(item, idx) in innerList" :key="idx" class="cj-upload__file-item">
          <i :class="fileIcon(item)"></i>
          <span class="cj-upload__file-name" :title="item.name">{{ item.name }}</span>
          <i v-if="!disableds" class="el-icon-close cj-upload__file-del" @click="handleRemove(item, idx)"></i>
        </li>
      </ul>
    </div>

    <!-- 上传入口（达上限时隐藏） -->
    <el-upload
      v-show="innerList.length < limit"
      ref="cjUpload"
      action="#"
      :auto-upload="false"
      :show-file-list="false"
      :on-change="handleChange"
      :on-exceed="handleExceed"
      :disabled="disableds || innerList.length >= limit"
      :accept="accept || ''"
    >
      <!-- 图片类型：卡片占位 -->
      <div v-if="isImage" class="cj-upload__trigger">
        <i class="el-icon-plus"></i>
      </div>
      <!-- 非图片类型：按钮 -->
      <el-button v-else size="small" type="primary" icon="el-icon-upload2">点击上传</el-button>
    </el-upload>

    <!-- 大图预览（隐藏的 el-image 触发器） -->
    <el-image
      ref="previewImg"
      style="width: 0; height: 0; position: fixed; top: -9999px; left: -9999px;"
      :src="previewUrl"
      :preview-src-list="previewList"
      :z-index="9999"
    />
  </div>
</template>

<script>
import { uploadFile } from '@/api/api'

// 通过文件名/类型判断图标
function getFileIcon (name) {
  const n = (name || '').toLowerCase()
  if (/\.(mp4|avi|mov|wmv|flv|mkv)$/.test(n)) return 'el-icon-video-camera'
  if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/.test(n)) return 'el-icon-picture'
  if (/\.(pdf)$/.test(n)) return 'el-icon-document'
  if (/\.(doc|docx)$/.test(n)) return 'el-icon-document'
  if (/\.(xls|xlsx)$/.test(n)) return 'el-icon-document'
  if (/\.(ppt|pptx)$/.test(n)) return 'el-icon-document'
  if (/\.(zip|rar|7z|tar|gz)$/.test(n)) return 'el-icon-folder'
  return 'el-icon-document'
}

export default {
  name: 'CjUpload',
  props: {
    // 允许的文件类型
    accept: { type: String, default: '' },
    // 回显的文件列表 [{ url, name }]
    filearr: { type: Array, default: () => [] },
    // 最大上传数
    limit: { type: Number, default: 1 },
    // 展示类型：picture-card 图片卡片 / text 按钮文本
    type: { type: String, default: 'picture-card' }
  },
  data () {
    return {
      // 组件内部维护的文件列表（独立于 el-upload 内部状态，确保回显/删除可控）
      innerList: [],
      uploading: false,
      // 预览相关
      previewUrl: '',
      previewList: []
    }
  },
  computed: {
    // 是否图片类型（决定卡片式还是列表式展示）
    isImage () {
      return this.type === 'picture-card'
    },
    // 是否禁用（只读模式）
    disableds () {
      return false
    }
  },
  watch: {
    // 监听外部 filearr 变化，同步到内部列表（初始化/编辑回显）
    filearr: {
      handler (val) {
        const incoming = (val || []).filter(f => f && f.url)
        // 仅当外部长度或 URL 与内部不一致时同步，避免上传中互相覆盖
        const sameLength = incoming.length === this.innerList.length
        const sameContent = sameLength && incoming.every((f, i) => f.url === this.innerList[i].url)
        if (!sameContent) {
          this.innerList = incoming.map(f => ({ url: f.url, name: f.name || this.extractName(f.url) }))
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 从 URL 提取文件名
    extractName (url) {
      if (!url) return '文件'
      const clean = url.split('?')[0]
      const segs = clean.split('/')
      return decodeURIComponent(segs[segs.length - 1] || '文件')
    },
    // 文件图标
    fileIcon (item) {
      return getFileIcon(item.name || item.url)
    },
    // 选择文件回调：校验后自动上传
    handleChange (file, fileList) {
      // 超过 limit 不处理
      if (this.innerList.length >= this.limit) {
        this.$message.warning(`最多上传 ${this.limit} 个文件`)
        return
      }
      // 重名校验
      const exists = this.innerList.find(f => f.name === file.name)
      if (exists) {
        this.$message.warning('当前文件已存在')
        return
      }
      this.doUpload(file)
    },
    // 执行上传
    async doUpload (file) {
      if (!file.raw) return
      this.uploading = true
      const loading = this.$loading({ lock: true, text: '上传中...', background: 'rgba(0,0,0,0.7)' })
      try {
        const fd = new FormData()
        fd.append('file', file.raw)
        const res = await uploadFile(fd)
        const url = res.data
        if (!url) {
          this.$message.error('上传失败：未返回文件地址')
          return
        }
        const item = { url, name: file.name || this.extractName(url) }
        // 追加到内部列表（触发视图更新 → 组件内回显）
        this.innerList.push(item)
        this.innerList = [...this.innerList]
        // 通知父组件
        this.$emit('fileChange', this.innerList.map(f => f.url))
        this.$message.success('上传成功')
      } catch (e) {
        // 错误提示已由请求拦截器处理
      } finally {
        this.uploading = false
        loading.close()
      }
    },
    // 删除文件
    handleRemove (item, idx) {
      this.innerList.splice(idx, 1)
      this.innerList = [...this.innerList]
      this.$emit('removeAfter', item.url)
    },
    // 预览图片
    handlePreview (item) {
      this.previewUrl = item.url
      this.previewList = this.innerList.map(f => f.url)
      this.$nextTick(() => {
        // 手动触发 el-image 的预览
        this.$refs.previewImg.showViewer = true
      })
    },
    // 超出上传限制
    handleExceed () {
      this.$message.warning(`最多上传 ${this.limit} 个文件`)
    }
  }
}
</script>

<style lang="scss" scoped>
.cj-upload {
  width: 100%;
}

/* 已上传列表 */
.cj-upload__list {
  margin-bottom: 10px;
}

/* 图片卡片 */
.cj-upload__item--card {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cj-upload__thumb {
  position: relative;
  width: 148px;
  height: 148px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
  }
}
.cj-upload__actions {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s;
  i {
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    &:hover { color: var(--fn-accent, #38a169); }
  }
}
.cj-upload__thumb:hover .cj-upload__actions {
  opacity: 1;
}

/* 文件列表 */
.cj-upload__file-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.cj-upload__file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  > i:first-child {
    color: var(--fn-primary, #1a365d);
    font-size: 16px;
  }
}
.cj-upload__file-name {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cj-upload__file-del {
  color: #ef4444;
  cursor: pointer;
  font-size: 14px;
  &:hover { color: #dc2626; }
}

/* 上传触发器（卡片占位） */
.cj-upload__trigger {
  width: 148px;
  height: 148px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s;
  i {
    font-size: 28px;
    color: #94a3b8;
  }
  &:hover {
    border-color: var(--fn-primary, #1a365d);
    background: #eef2f6;
    i { color: var(--fn-primary, #1a365d); }
  }
}

::v-deep .el-upload--picture-card {
  display: none;
}
::v-deep .el-upload-list {
  display: none;
}
</style>
